import "./style.scss";

import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import { Modal } from "@mui/material";
import Stepper from "components/shared/Stepper";
import { cloneElement, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import variables from "../../../assets/styles/_variables.module.scss";

type TStyled = {
    mainTitle: string | undefined;
    subTitle: string | undefined;
    controlsTitle: string | undefined;
    className?: string;
};

type TProps = {
    open: boolean;
    onClose: () => void;
    children: JSX.Element;
    mainTitle?: string;
    subTitle?: string;
    controlsTitle?: string;
    Icon?: any;
    showCloseButton?: boolean;
    noPadding?: boolean;
    steps?: string[];
};

const ModalHeader = styled.header<TStyled>((props) => ({
    borderBottom: !(props.mainTitle || props.subTitle || props.controlsTitle)
        ? "none"
        : `1px solid ${variables["border-grey"]}`,
}));

const ModalContainer = styled("div")`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #fff;
    border-radius: 8px;
    padding: 0;
    width: 80%;
`;
const ModalTitle = styled.div<TStyled>((props) => ({
    display: "flex",
    gap: "16px",
    alignItems: "center",
    // width: !(props.mainTitle || props.subTitle || props.controlsTitle) ? "100%" : "auto",
    width: "auto",
    justifyContent: !(props.mainTitle || props.subTitle || props.controlsTitle) ? "center" : "flex-start",
}));

/**
 * Modal in Payment page to check payments for bank transfer and download file.
 */
const BankTransferModal = ({
    open,
    onClose,
    mainTitle,
    subTitle,
    controlsTitle,
    children,
    Icon,
    showCloseButton = true,
    noPadding = false,
    steps: propSteps,
}: TProps) => {
    const [t] = useTranslation();
    const [currentTab, setCurrentTab] = useState(0);
    const steps = propSteps ?? [
        t("PaymentModal.MethodStep"),
        t("PaymentModal.PreppingStep"),
        t("PaymentModal.PaymentStep"),
    ];

    const moveToStep = useCallback(
        (forward = false) =>
            () => {
                if (forward) {
                    setCurrentTab((value) => ++value);
                } else {
                    setCurrentTab((prev) => --prev);
                }
            },
        [],
    );

    return (
        <Modal
            open={open}
            onClose={() => {
                onClose();
                setCurrentTab(0);
            }}
        >
            <ModalContainer>
                <div className="payment-modal-wrapper">
                    <ModalHeader
                        mainTitle={mainTitle}
                        subTitle={subTitle}
                        controlsTitle={controlsTitle}
                        className="modal-header"
                    >
                        <ModalTitle
                            mainTitle={mainTitle}
                            subTitle={subTitle}
                            controlsTitle={controlsTitle}
                            className="modal-header-titles"
                        >
                            {Icon && <Icon />}
                            <div className="modal-header-info-titles">
                                <h2 className="main-title">{mainTitle}</h2>
                                {subTitle && <h4 className="sub-title">{subTitle}</h4>}
                            </div>
                        </ModalTitle>
                        <header className="import-data-header">
                            <Stepper currentStep={currentTab} steps={steps} />
                        </header>
                        <div className="modal-header-controls">
                            {controlsTitle && <h4 className="title">{controlsTitle}</h4>}
                            {showCloseButton && (
                                <CloseIcon
                                    onClick={() => {
                                        onClose();
                                        setCurrentTab(0);
                                    }}
                                    sx={{
                                        transition: "0.4s",
                                        cursor: "pointer",
                                        color: "#B4B4B4",
                                        "&:hover": {
                                            color: "#626262",
                                        },
                                    }}
                                />
                            )}
                        </div>
                    </ModalHeader>
                    <main className="payment-modal-main" style={noPadding ? { padding: 0 } : {}}>
                        {cloneElement(children, { moveToStep, currentTab, setCurrentTab })}
                    </main>
                </div>
            </ModalContainer>
        </Modal>
    );
};

export default BankTransferModal;
